import React, { useEffect, useState, useRef } from 'react';

import { useNavigate } from 'react-router-dom';

import { Helmet } from 'react-helmet';


import LoadingWindow from './windows/LoadingWindow';
import AboutIcon from './icons/AboutIcon';
import PortfolioIcon from './icons/PortfolioIcon';
import GalleryIcon from './icons/GalleryIcon';
import NewsIcon from './icons/NewsIcon';
import ContactIcon from './icons/ContactIcon';
import RecycleIcon from './icons/RecycleIcon';
import PortfolioWindow from './windows/PortfolioWindow';
import AboutWindow from './windows/AboutWindow'; // ⬅️ Import AboutWindow
import ContactWindow from './windows/ContactWindow';
import GalleryWindow from './windows/GalleryWindow';
import NewsWindow from './windows/NewsWindow';
import RecycleWindow from './windows/RecycleWindow';
import ParticleTrail from './ParticleTrail';

const iconComponents = {
    about: AboutIcon,
    portfolio: PortfolioIcon,
    gallery: GalleryIcon,
    news: NewsIcon,
    contact: ContactIcon,
    recycle: RecycleIcon
};

const ApplicationContainer = ({
    isAboutOpen, setIsAboutOpen,
    isPortfolioOpen, setIsPortfolioOpen,
    isContactOpen, setIsContactOpen,
    isGalleryOpen, setIsGalleryOpen,
    isNewsOpen, setIsNewsOpen,
    isRecycleOpen, setIsRecycleOpen,
    startMenuOpen, setStartMenuOpen,
    zFILO, addToZFILO, removeFromZFILO
}) => {
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(true);

    const bgRef = useRef(null);

    useEffect(() => {
        const handleMouseMove = (e) => {
            const x = e.clientX / window.innerWidth;
            const y = e.clientY / window.innerHeight;

            if (bgRef.current) {
                bgRef.current.style.background = `radial-gradient(circle at ${x * 100}% ${y * 100}%,#DEE5F700,#DEE5F7)`;
            }
        };

        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    const generateIcons = (width, height) => {
        const isMobile = width <= 630;
        const iconTypes = ['about', 'portfolio', 'news', 'contact', 'gallery', 'recycle'];

        if (isMobile) {
            const columns = 2;
            const spacingX = width / columns - 40;
            const spacingY = 160;

            return iconTypes.map((type, index) => {
                const col = index % columns;
                const row = Math.floor(index / columns);
                return {
                    id: index + 1,
                    type,
                    position: {
                        x: col * spacingX + spacingX / 2 - 60,
                        y: row * spacingY + 20
                    }
                };
            });
        } else {
            return [
                { id: 1, type: 'about', position: { x: 30, y: 30 } },
                { id: 2, type: 'portfolio', position: { x: 200, y: 30 } },
                { id: 3, type: 'gallery', position: { x: 30, y: 370 } },
                { id: 4, type: 'news', position: { x: 30, y: 200 } },
                { id: 5, type: 'contact', position: { x: 200, y: 200 } },
                {
                    id: 6,
                    type: 'recycle',
                    position: {
                        x: width - 180,
                        y: height - 280
                    }
                }
            ];
        }
    };

    const [icons, setIcons] = useState();
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        setIcons(generateIcons(windowSize.width, windowSize.height));
    }, [windowSize]);

    const wasPortfolioDraggedRef = useRef(false);
    const wasAboutDraggedRef = useRef(false);
    const wasGalleryDraggedRef = useRef(false);
    const wasNewsDraggedRef = useRef(false);
    const wasContactDraggedRef = useRef(false);
    const wasRecycleDraggedRef = useRef(false);

    const draggingRef = useRef(null);
    const offsetRef = useRef({ x: 0, y: 0 });

    const handleMouseDown = (e, id) => {
        draggingRef.current = id;
        const icon = icons.find(i => i.id === id);
        offsetRef.current = {
            x: e.clientX - icon.position.x,
            y: e.clientY - icon.position.y
        };
        if (icon.type === 'portfolio') wasPortfolioDraggedRef.current = false;
        if (icon.type === 'about') wasAboutDraggedRef.current = false;
        if (icon.type === 'gallery') wasGalleryDraggedRef.current = false;
        if (icon.type === 'news') wasNewsDraggedRef.current = false;
        if (icon.type === 'contact') wasContactDraggedRef.current = false;
        if (icon.type === 'recycle') wasRecycleDraggedRef.current = false;

    };

    const handleMouseMove = (e) => {
        if (draggingRef.current === null) return;
        const id = draggingRef.current;
        const draggedIcon = icons.find(icon => icon.id === id);

        if (draggedIcon?.type === 'portfolio') wasPortfolioDraggedRef.current = true;
        if (draggedIcon?.type === 'about') wasAboutDraggedRef.current = true;
        if (draggedIcon?.type === 'gallery') wasGalleryDraggedRef.current = true;
        if (draggedIcon?.type === 'news') wasNewsDraggedRef.current = true;
        if (draggedIcon?.type === 'contact') wasContactDraggedRef.current = true;
        if (draggedIcon?.type === 'recycle') wasRecycleDraggedRef.current = true;


        setIcons(prev =>
            prev.map(icon =>
                icon.id === id
                    ? {
                        ...icon,
                        position: {
                            x: e.clientX - offsetRef.current.x,
                            y: e.clientY - offsetRef.current.y
                        }
                    }
                    : icon
            )
        );
    };

    const handleMouseUp = () => {
        draggingRef.current = null;
    };

    const handleTouchStart = (e, id) => {
        const touch = e.touches[0];
        handleMouseDown({ clientX: touch.clientX, clientY: touch.clientY }, id);
    };

    const handleTouchMove = (e) => {
        if (draggingRef.current === null) return;
        const touch = e.touches[0];
        handleMouseMove({ clientX: touch.clientX, clientY: touch.clientY });
    };

    const handleTouchEnd = () => {
        handleMouseUp();
    };

    return (
        <div
            className='ApplicationContainer'
            ref={bgRef}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseDown={() => {
                if (startMenuOpen) {
                    setStartMenuOpen(false);
                }
            }}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        ><ParticleTrail />
            {isLoading ? (
                <LoadingWindow setIsLoading={setIsLoading} />
            ) : (
                <>
                    {icons.map(icon => {
                        const IconComponent = iconComponents[icon.type];
                        return (
                            <div
                                key={icon.id}
                                onMouseDown={(e) => handleMouseDown(e, icon.id)}
                                onMouseUp={() => {
                                    handleMouseUp();
                                    if (icon.type === 'portfolio' && !wasPortfolioDraggedRef.current) {
                                        setIsPortfolioOpen(false);
                                        removeFromZFILO('portfolio');
                                        setIsPortfolioOpen(true);
                                        addToZFILO('portfolio');
                                        navigate('/' + icon.type);
                                    }
                                    if (icon.type === 'about' && !wasAboutDraggedRef.current) {
                                        setIsAboutOpen(false);
                                        removeFromZFILO('about');
                                        setIsAboutOpen(true);
                                        addToZFILO('about');
                                        navigate('/' + icon.type);
                                    }
                                    if (icon.type === 'contact' && !wasContactDraggedRef.current) {
                                        setIsContactOpen(false);
                                        removeFromZFILO('contact');
                                        setIsContactOpen(true);
                                        addToZFILO('contact');
                                        navigate('/' + icon.type);
                                    }
                                    if (icon.type === 'gallery' && !wasGalleryDraggedRef.current) {
                                        setIsGalleryOpen(false);
                                        removeFromZFILO('gallery');
                                        setIsGalleryOpen(true);
                                        addToZFILO('gallery');
                                        navigate('/' + icon.type);
                                    }
                                    if (icon.type === 'news' && !wasNewsDraggedRef.current) {
                                        setIsNewsOpen(false);
                                        removeFromZFILO('news');
                                        setIsNewsOpen(true);
                                        addToZFILO('news');
                                        navigate('/' + icon.type);
                                    }
                                    if (icon.type === 'recycle' && !wasRecycleDraggedRef.current) {
                                        setIsRecycleOpen(false);
                                        removeFromZFILO('recycle');
                                        setIsRecycleOpen(true);
                                        addToZFILO('recycle');
                                        navigate('/' + icon.type);
                                    }

                                }}
                                onTouchStart={(e) => handleTouchStart(e, icon.id)}
                                style={{
                                    position: 'absolute',
                                    left: icon.position.x,
                                    top: icon.position.y,
                                    cursor: 'grab',
                                    touchAction: 'none'
                                }}
                            >
                                <IconComponent />
                            </div>
                        );
                    })}
                    {isPortfolioOpen && <PortfolioWindow isPortfolioOpen={isPortfolioOpen} setIsPortfolioOpen={setIsPortfolioOpen} zFILO={zFILO} addToZFILO={addToZFILO} removeFromZFILO={removeFromZFILO} />}
                    {isAboutOpen && <AboutWindow isAboutOpen={isAboutOpen} setIsAboutOpen={setIsAboutOpen} zFILO={zFILO} addToZFILO={addToZFILO} removeFromZFILO={removeFromZFILO} />}
                    {isContactOpen && <ContactWindow isContactOpen={isContactOpen} setIsContactOpen={setIsContactOpen} zFILO={zFILO} addToZFILO={addToZFILO} removeFromZFILO={removeFromZFILO} />}
                    {isGalleryOpen && <GalleryWindow isGalleryOpen={isGalleryOpen} setIsGalleryOpen={setIsGalleryOpen} zFILO={zFILO} addToZFILO={addToZFILO} removeFromZFILO={removeFromZFILO} />}
                    {isNewsOpen && <NewsWindow isNewsOpen={isNewsOpen} setIsNewsOpen={setIsNewsOpen} zFILO={zFILO} addToZFILO={addToZFILO} removeFromZFILO={removeFromZFILO} />}
                    {isRecycleOpen && <RecycleWindow isRecycleOpen={isRecycleOpen} setIsRecycleOpen={setIsRecycleOpen} zFILO={zFILO} addToZFILO={addToZFILO} removeFromZFILO={removeFromZFILO} />}
                </>
            )}
        </div>
    );
};

export default ApplicationContainer;
